import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import { Visibility, VisibilityOff, Person, Lock } from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import RecruitmentPopup from "../../components/common/RecruitmentPopup";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);

  const MAX_ATTEMPTS_BEFORE_HINT = 3;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const data = await login(formData.username, formData.password);

      if (!data || !data.user) {
        throw new Error(
          (data && data.message) || "Respuesta inválida del servidor de login",
        );
      }

      if (data.user.rol === "administrador") {
        navigate("/admin/dashboard");
      } else if (data.user.rol === "colaborador") {
        navigate("/colaborador/dashboard");
      }
    } catch (err) {
      const backendMsg = err.response?.data?.message;
      const fallback = err.message || "Error al iniciar sesión";
      setError(backendMsg || fallback || "Error al iniciar sesión");
      setFailedAttempts((prev) => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)",
        padding: 2,
      }}
    >
      <RecruitmentPopup />
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            background: "rgba(19, 24, 41, 0.9)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: 3,
          }}
        >
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              className="gradient-text"
              sx={{ fontWeight: 700 }}
            >
              A la Vuelta 360°
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sistema de Gestión de Eventos
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Usuario"
              name="username"
              value={formData.username}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="username"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Contraseña"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: "right", mt: 1, mb: 2 }}>
              {failedAttempts >= MAX_ATTEMPTS_BEFORE_HINT ? (
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate("/forgot-password")}
                  sx={{
                    color: "#90caf9",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              ) : failedAttempts > 0 ? (
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.28)", userSelect: "none" }}>
                  ¿Olvidaste tu contraseña? disponible tras {MAX_ATTEMPTS_BEFORE_HINT - failedAttempts} intento{MAX_ATTEMPTS_BEFORE_HINT - failedAttempts !== 1 ? "s" : ""} más
                </Typography>
              ) : null}
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                background: "linear-gradient(135deg, #00C9FF 0%, #92FE9D 100%)",
                color: "#000",
                fontWeight: 600,
                py: 1.5,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #0098CC 0%, #6FCA7D 100%)",
                },
              }}
            >
              {loading ? "Iniciando sesión..." : "Iniciar Sesión"}
            </Button>
          </form>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              © 2026 A la Vuelta 360°. Todos los derechos reservados.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
